import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import * as formHelpers from '../../scripts/formHelpers';
import * as formValidators from '../../scripts/formValidators';
import { MoneyField, NumberField } from '../shared/formFields';
import {
  MotifButton,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifIcon,
  MotifFormField,
  MotifCheckbox,
  MotifTooltip,
  MotifErrorMessage,
} from '@ey-xd/motif-react';
import { IconoirInfoCircle } from '@ey-xd/motif-icon';
import * as formParsers from '../../scripts/formParsers';

const editSecuritiesSnapshotForm = ({
  handleSubmit,
  handleCloseForm,
  initialValues,
  formPurpose,
  error,
  submitting,
  handleDismissErrorMessage,
  isTenPercentValueTestDisabledFlag,
  handleDisableTenPercentValueTestSelection,
}) => {
  const closeButtonProps = {
    title: 'Close Edit Snapshot Modal',
    ariaLabel: 'Close Edit Snapshot Modal',
    dataTestid: 'close',
  };

  const HeaderWrapper = ({ children }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'center',
      }}
    >
      {children}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="securitiesSnapshot">
      <MotifModalHeader closeButtonProps={closeButtonProps}>
        <HeaderWrapper>
          <span>Edit Snapshot</span>
        </HeaderWrapper>
      </MotifModalHeader>
      <MotifModalBody>
        {formPurpose === 'EDIT' &&
          initialValues.hasIncompleteSecuritySnapshot && (
            <MotifErrorMessage>
              This is a system-generated security from the uploaded trial
              balance. Please disable the 10% test or input the 'Value of all
              Shares from Issuer' to resolve the security diagnostics.
            </MotifErrorMessage>
          )}
        <Field
          name="securitiesSnapshotID"
          type="hidden"
          component="input"
          label=""
        />
        <Field name="reportPeriodID" type="hidden" component="input" label="" />
        <div className="row mt-3">
          <br />
          {formHelpers.showSubmissionError(error, handleDismissErrorMessage)}
          <div className="field col-md-6">
            <Field
              name="reitOrPropertyName"
              type="text"
              component={formHelpers.renderField}
              label="REIT or Property Name"
              disabled={'disabled'}
              data-testid="txtReitOrPropertyName"
            />
          </div>
          <div className="field col-md-6">
            <Field
              name="clientAccountNumber"
              type="text"
              component={formHelpers.renderField}
              label="Client Account Number"
              disabled={'disabled'}
              data-testid="txtClientAccountNumber"
            />
          </div>
        </div>
        <div className="row mt-3">
          <div className="field col-md-6">
            <Field
              name="securityName"
              type="text"
              component={formHelpers.renderField}
              label="Name"
              disabled={'disabled'}
              maxLength={255}
              isRequiredField={true}
              validate={[
                formValidators.required,
                formValidators.maxLength255,
                formValidators.noWindowSpecialChars,
              ]}
              data-testid="txtSecurityName"
            />
          </div>
          <div className="field col-md-6">
            <Field
              name="issuer"
              type="text"
              component={formHelpers.renderField}
              label="Issuer"
              disabled={'disabled'}
              maxLength={255}
              isRequiredField={true}
              validate={[formValidators.required, formValidators.maxLength255]}
              data-testid="txtIssuer"
            />
          </div>
        </div>
        <div className="row">
          <div className="field col-md-6">
            <Field
              name="class"
              type="text"
              component={formHelpers.renderField}
              label="Class"
              disabled={'disabled'}
              isRequiredField={true}
              maxLength={255}
              validate={[formValidators.required, formValidators.maxLength255]}
              data-testid="txtClass"
            />
          </div>

          <div className="field col-md-6">
            <MoneyField
              name="reitOwnedSharesValue"
              placeholder="0.00"
              label="Owned Shares Value"
              disabled={
                (initialValues?.clientAccountMappingID !== null &&
                  initialValues?.clientAccountMappingID !== undefined) ||
                initialValues?.isSystemGenerated
                  ? 'disabled'
                  : ''
              }
              isRequiredField={true}
              validate={[
                formValidators.required,
                formValidators.number,
                formValidators.maxValueDecimal,
                formValidators.zeroOrGreater,
              ]}
              data-testid="curOwnedSharesValue"
            />
          </div>
        </div>
        <div className="row mt-3">
          <div className="field col-md-4">
            <MotifFormField>
              <MotifCheckbox
                type="checkbox"
                data-testid="chkbxTenPercentValueTest"
                name="tenPercentValueTest"
                id="tenPercentValueTest"
                checked={isTenPercentValueTestDisabledFlag}
                onChange={(event) => {
                  handleDisableTenPercentValueTestSelection(
                    event.target.checked
                  );
                }}
              >
                Disable 10% Value Test
              </MotifCheckbox>
            </MotifFormField>
          </div>
          <div className="field col-md-8" style={{ marginLeft: '-40px' }}>
            <MotifTooltip
              trigger={<MotifIcon iconFunction={IconoirInfoCircle} size="24" />}
            >
              <div>
                The 'Disable 10% Value Test' checkbox allows you to exempt
                <br />
                a security from the 10% value test. When this box is checked,
                <br />
                the system will not perform the standard 10% value test.
              </div>
            </MotifTooltip>
          </div>
        </div>
        {isTenPercentValueTestDisabledFlag === false && (
          <div className="row">
            <div className="field col-md-12">
              <MoneyField
                name="allSharesFromIssuerValue"
                placeholder="0.00"
                label="Value of all Shares from Issuer"
                isRequiredField={true}
                validate={[
                  formValidators.required,
                  formValidators.number,
                  formValidators.maxValueDecimal,
                  formValidators.greaterThan0,
                ]}
                data-testid="curAllSharesFromIssuerValue"
              />
            </div>
          </div>
        )}
        <div className="row">
          <div className="field col-md-12">
            <NumberField
              name="percentOfVote"
              placeholder="0.00"
              label="Percent of Vote"
              allowDecimal={true}
              isRequiredField={true}
              validate={[
                formValidators.required,
                formValidators.number,
                formValidators.percentBetween0And100,
              ]}
              data-testid="numPercentOfVote"
              parse={formParsers.positiveNumberParser} // add this parse to prevent accepting negative (-) value
            />
          </div>
        </div>
      </MotifModalBody>
      <MotifModalFooter>
        <MotifButton
          variant="secondary"
          type="button"
          onClick={handleCloseForm}
          data-testid="btnCancel"
        >
          Cancel
        </MotifButton>
        <MotifButton
          variant="primary"
          type="submit"
          value="Submit"
          disabled={submitting}
          data-testid="btnSave"
        >
          <React.Fragment>Save</React.Fragment>
        </MotifButton>
      </MotifModalFooter>
    </form>
  );
};

editSecuritiesSnapshotForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  handleCloseForm: PropTypes.func.isRequired,
  handleDismissErrorMessage: PropTypes.func,
  formPurpose: PropTypes.string,
  initialValues: PropTypes.object,
  error: PropTypes.string,
  submitting: PropTypes.bool,
};

export default reduxForm({ form: 'securitiesSnapshot' })(
  editSecuritiesSnapshotForm
);
